/**
 * Detección de valores PARECIDOS — el mecanismo anti-typo.
 *
 * ── Por qué existe ──────────────────────────────────────────────────────────
 *
 * El backend unifica solo lo que es demostrablemente el mismo valor: «Blanco»,
 * «blanco» y « BLANCO » son la misma palabra escrita distinto y se colapsan
 * solas (backend/src/utils/etiquetaTexto.js).
 *
 * Pero «vlanco» no. Es un error de tipeo y, al mismo tiempo, un texto
 * perfectamente legítimo: nada en el valor permite distinguir un dedazo de una
 * palabra nueva de verdad. Corregirlo automáticamente sería adivinar, y
 * adivinar sobre los datos de otro es peor que dejar el duplicado.
 *
 * Lo que sí se puede hacer es AVISAR: mostrar el parecido y dejar que quien
 * está escribiendo decida en un click. Esa es la única forma correcta de
 * resolver esta mitad del problema.
 *
 * ── Por qué las reglas son conservadoras ────────────────────────────────────
 *
 * Un aviso que aparece de más es peor que no tener aviso: se aprende a
 * ignorarlo, y el día que acierta ya nadie lo mira. De ahí los tres frenos:
 * mínimo de largo, tolerancia proporcional, y nada de sugerir palabras que
 * simplemente se están terminando de escribir.
 */

import { norm } from "./fuzzyMatch";

/**
 * Distancia de edición (Levenshtein) entre dos cadenas ya normalizadas.
 *
 * Con corte temprano: si toda la fila supera el máximo aceptable, no tiene
 * sentido terminar de calcular — solo interesa saber si está CERCA.
 */
export function distanciaEdicion(a: string, b: string, maximo = Infinity): number {
  if (a === b) return 0;
  // Diferencia de largo: cota inferior de la distancia. Descarta al toque los
  // pares que ni siquiera podrían entrar.
  if (Math.abs(a.length - b.length) > maximo) return maximo + 1;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let anterior = Array.from({ length: b.length + 1 }, (_, i) => i);
  let actual = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    actual[0] = i;
    let minFila = actual[0];
    for (let j = 1; j <= b.length; j++) {
      const costo = a[i - 1] === b[j - 1] ? 0 : 1;
      actual[j] = Math.min(
        actual[j - 1] + 1, // inserción
        anterior[j] + 1, // borrado
        anterior[j - 1] + costo, // sustitución
      );
      if (actual[j] < minFila) minFila = actual[j];
    }
    if (minFila > maximo) return maximo + 1;
    const tmp = anterior;
    anterior = actual;
    actual = tmp;
  }
  return anterior[b.length];
}

/**
 * Cuánta diferencia se tolera para considerar dos valores "el mismo con un
 * dedazo". Proporcional al largo: en una palabra corta, dos letras de
 * diferencia ya son otra palabra.
 */
function toleranciaPara(largo: number): number {
  if (largo <= 5) return 1;
  if (largo <= 9) return 2;
  return 3;
}

/** Largo mínimo para siquiera intentar. Debajo de esto, casi todo se parece a
 *  casi todo y el aviso sería puro ruido. */
const LARGO_MINIMO = 4;

/**
 * Valores existentes que se parecen al escrito, sin ser el mismo.
 *
 * Devuelve vacío —a propósito— en los casos en que un aviso estorbaría:
 *
 *   • el valor es muy corto (ver `LARGO_MINIMO`);
 *   • ya coincide EXACTO con uno existente ignorando mayúsculas y acentos: ahí
 *     no hay nada que sugerir, el backend lo unifica solo;
 *   • el candidato EMPIEZA con lo escrito («Sában» → «Sábana»). Eso no es un
 *     typo, es una palabra a medio escribir, y el desplegable ya la está
 *     mostrando. Avisar ahí convierte el aviso en ruido constante.
 *
 * @param valor lo que se escribió
 * @param candidatos valores que ya existen en esa columna
 * @param limite cuántos devolver como máximo
 */
export function parecidosA(
  valor: string | null | undefined,
  candidatos: readonly string[] | null | undefined,
  limite = 2,
): string[] {
  const v = norm(valor);
  if (v.length < LARGO_MINIMO) return [];
  if (!candidatos?.length) return [];

  const tolerancia = toleranciaPara(v.length);
  const cercanos: Array<{ texto: string; distancia: number }> = [];

  for (const candidato of candidatos) {
    const c = norm(candidato);
    if (!c) continue;
    // Coincidencia exacta: no hay typo que avisar.
    if (c === v) return [];
    if (c.startsWith(v)) continue;
    const distancia = distanciaEdicion(v, c, tolerancia);
    if (distancia > 0 && distancia <= tolerancia) {
      cercanos.push({ texto: String(candidato), distancia });
    }
  }

  return cercanos
    .sort((a, b) => a.distancia - b.distancia || a.texto.localeCompare(b.texto, "es"))
    .slice(0, limite)
    .map((x) => x.texto);
}
